import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";
import { ChevronRight, Scale, X, ArrowRight } from "lucide-react";
import { getCompare, removeFromCompare, clearCompare } from "../lib/compareStore";
import QuoteWizard from "../components/QuoteWizard";
import { Button } from "../components/ui/button";

export default function ComparisonPage() {
  useSeo({ title: "Ürün Karşılaştırma — Otel Pro", description: "Yan yana ürün karşılaştırma aracı." });
  const [items, setItems] = useState(getCompare());
  const [products, setProducts] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);

  const reload = () => setItems(getCompare());

  useEffect(() => {
    window.addEventListener("compare:update", reload);
    return () => window.removeEventListener("compare:update", reload);
  }, []);

  useEffect(() => {
    if (items.length >= 2) {
      const ids = items.map((i) => i.id);
      api.post("/comparison/compare", { product_ids: ids }).then((r) => setProducts(r.data.products || []));
    } else {
      setProducts([]);
    }
  }, [items]);

  const badgeLabels = { premium: "Premium", logo_baskili: "Logo Baskılı", toptan: "Toptan", stokta: "Stokta" };

  return (
    <Layout>
      <div className="bg-softbg">
        <div className="container-site py-5 flex items-center gap-2 text-xs text-gray-500">
          <Link to="/" className="hover:text-navy">Ana Sayfa</Link>
          <ChevronRight size={12} />
          <span className="text-navy font-medium">Karşılaştırma</span>
        </div>
      </div>

      <section className="bg-navy text-white py-16">
        <div className="container-site max-w-3xl">
          <div className="flex items-center gap-3 mb-3">
            <Scale className="text-gold" size={26} />
            <p className="accent-script text-gold text-2xl">Benzersiz Araç</p>
          </div>
          <h1 className="heading-serif text-3xl md:text-5xl font-semibold">Ürün Karşılaştırma</h1>
          <p className="text-white/75 mt-4">
            En fazla 3 ürünü yan yana karşılaştırarak doğru ekipmanı seçin. Teknik özellikler, avantajlar ve yıldız uyumluluğu bir arada.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-site">
          {items.length < 2 ? (
            <div className="text-center py-16">
              <Scale size={40} className="mx-auto text-gold mb-4" />
              <h2 className="heading-serif text-2xl font-semibold text-navy mb-2">Henüz karşılaştırma listesi boş</h2>
              <p className="text-gray-500 mb-6">Ürün kartlarındaki <span className="inline-flex items-center gap-1 text-teal"><Scale size={14} /> Karşılaştır</span> ikonuyla 2-3 ürün ekleyin.</p>
              <Link to="/kategoriler" className="btn-gold" data-testid="compare-empty-cta">Ürünlere Git <ArrowRight size={16} /></Link>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="compare-table-wrapper">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{items.length} ürün karşılaştırılıyor</p>
                <div className="flex gap-2">
                  <button onClick={clearCompare} className="text-xs text-gray-500 hover:text-rose-600" data-testid="compare-clear-all">Listeyi Temizle</button>
                </div>
              </div>
              <table className="w-full min-w-[640px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead>
                  <tr>
                    <th className="w-40 bg-softbg p-4 text-left text-xs uppercase tracking-wider text-gray-500">Ürün</th>
                    {products.map((p) => (
                      <th key={p.id} className="p-4 align-top text-left min-w-[220px] bg-softbg" data-testid={`compare-col-${p.slug}`}>
                        <div className="relative">
                          <button onClick={() => removeFromCompare(p.id)} className="absolute top-0 right-0 text-gray-400 hover:text-rose-600">
                            <X size={14} />
                          </button>
                          {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full aspect-[4/3] object-cover rounded-md mb-3" />}
                          <div className="heading-serif text-navy font-semibold text-sm">{p.name}</div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(p.badges || []).map((b) => (
                              <span key={b} className={`badge-${b === "logo_baskili" ? "logo" : b === "toptan" ? "toptan" : "premium"} !text-[10px] !px-2 !py-0.5`}>
                                {badgeLabels[b] || b}
                              </span>
                            ))}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <Row label="Kısa Açıklama" values={products.map((p) => p.short_description)} />
                  <Row label="Minimum Sipariş" values={products.map((p) => p.min_order)} />
                  <Row label="Avantajlar" values={products.map((p) => (p.features || []).map((f, i) => <div key={i} className="text-xs mb-1">✦ {f}</div>))} />
                  {/* Specs */}
                  {(() => {
                    const keys = new Set();
                    products.forEach((p) => Object.keys(p.specs || {}).forEach((k) => keys.add(k)));
                    return Array.from(keys).map((k) => (
                      <Row key={k} label={k} values={products.map((p) => p.specs?.[k] || "-")} />
                    ));
                  })()}
                </tbody>
              </table>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={() => setWizardOpen(true)} className="bg-gold hover:bg-[#c49d58]" data-testid="compare-quote-all">
                  Tümü İçin Teklif İste
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const r = await api.post(
                        "/comparison/pdf",
                        { product_ids: products.map((p) => p.id) },
                        { responseType: "blob" }
                      );
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "otel-pro-karsilastirma.pdf";
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { /* ignored */ }
                  }}
                  data-testid="compare-download-pdf"
                >
                  PDF İndir
                </Button>
                <Button variant="outline" onClick={() => window.print()} data-testid="compare-print">Yazdır</Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <QuoteWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialSource="comparison"
        initialItems={products.map((p) => ({ product_id: p.id, name: p.name, quantity: 1 }))}
        initialNotes="Karşılaştırma aracından iletilen ürünler."
      />
    </Layout>
  );
}

const Row = ({ label, values }) => (
  <tr className="border-t border-gray-100">
    <td className="p-4 font-medium text-gray-600 bg-softbg text-xs uppercase tracking-wider align-top">{label}</td>
    {values.map((v, i) => <td key={i} className="p-4 align-top text-navy">{v}</td>)}
  </tr>
);
